<template>
  <div class="access">
    <div class="access__card">
      <div v-if="attendee?.logo_url" class="access__logo">
        <img :src="attendee?.logo_url" alt="Event Logo" width="150" />
      </div>

      <div v-if="events">
        <h3 class="access__title">My Events</h3>

        <div class="search-box">
          <c-input
            placeholder="Search Events by Name..."
            v-model="searchQuery"
          />
        </div>

        <!-- Current Events -->
        <div v-if="filteredEvents?.current?.length" class="events-section">
          <h2 class="events-section__title">Current Events</h2>
          <div
            v-for="event in filteredEvents.current"
            :key="event.id"
            class="event-card"
            @click="handleCardClick(event.id)"
          >
            <div class="event-card__content">
              <div class="event-card__logo" v-if="event.event_logo_url">
                <img :src="event.event_logo_url" alt="Event Logo" />
              </div>
              <div class="event-card__info">
                <h3 class="event-card__name">{{ event.name }}</h3>
                <p class="event-card__date">{{ event.date }}</p>
              </div>
            </div>
            <i class="fa-solid fa-chevron-right event-card__arrow"></i>
          </div>
        </div>

        <!-- Future Events -->
        <div v-if="filteredEvents?.future?.length" class="events-section">
          <h2 class="events-section__title">Future Events</h2>
          <div
            v-for="event in filteredEvents.future"
            :key="event.id"
            class="event-card"
            @click="handleCardClick(event.id)"
          >
            <div class="event-card__content">
              <div class="event-card__logo" v-if="event.event_logo_url">
                <img :src="event.event_logo_url" alt="Event Logo" />
              </div>
              <div class="event-card__info">
                <h3 class="event-card__name">{{ event.name }}</h3>
                <p class="event-card__date">{{ event.date }}</p>
              </div>
            </div>
            <i class="fa-solid fa-chevron-right event-card__arrow"></i>
          </div>
        </div>

        <!-- Past Events -->
        <div v-if="filteredEvents?.past?.length" class="events-section">
          <h2 class="events-section__title">Past Events</h2>
          <div
            v-for="event in filteredEvents.past"
            :key="event.id"
            class="event-card-wrapper"
            @touchstart="onTouchStart($event, event)"
            @touchmove="onTouchMove"
            @touchend="onTouchEnd"
          >
            <div
              class="event-card event-card--past"
              :style="getCardStyle(event.id)"
              @click="handlePastEventClick(event)"
            >
              <div class="event-card__content">
                <div class="event-card__logo" v-if="event.event_logo_url">
                  <img :src="event.event_logo_url" alt="Event Logo" />
                </div>
                <div class="event-card__info">
                  <h3 class="event-card__name">{{ event.name }}</h3>
                  <p class="event-card__date">{{ event.date }}</p>
                </div>
              </div>

              <div class="event-card__actions">
                <!-- десктопна кнопка Delete -->
                <div @click.stop>
                  <c-button
                    class="desktop-delete-btn"
                    theme="default"
                    icon="fa-trash-can"
                    size="sm"
                    uppercase
                    @click="handleDeleteClick(event)"
                  ></c-button>
                </div>

                <i class="fa-solid fa-chevron-right event-card__arrow"></i>
              </div>
            </div>

            <!-- мобільна кнопка Delete при свайпі -->
            <div class="delete-action">
              <button class="delete-button" @click.stop="openRemoveModal(event)">
                <i class="fa fa-trash-can"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- No Events -->
        <div v-if="noEvents" class="no-events">
          <p>No events available</p>
        </div>
      </div>

      <div v-else class="page-loader">
        <c-loader />
      </div>
    </div>

    <c-confirm-delete-modal
      :showIndicator="showDeleteIndicator"
      :isOpen="showConfirmRemoveModal"
      @close="closeRemoveModal"
      @confirm="removeEvent"
      title="Remove Event"
      description="Are you sure you want to delete this event?"
      :body="currentEvent ? currentEvent.name : ''"
    />
  </div>
</template>

<script>
import accessAPI from "../../../api/accessAPI";
import {storage} from "../../../helpers/storage";
import {setHeaderAuth} from "../../../api";

export default {
  data() {
    return {
      event: '',
      showIndicator: false,
      eventOptions: [],
      events: null,
      searchQuery: '',
      showConfirmRemoveModal: false,
      showDeleteIndicator: false,
      currentEvent: null,
      // Swipe functionality
      touchStartX: 0,
      touchStartY: 0,
      touchCurrentX: 0,
      swipedEventId: null,
      isSwiping: false,
      hasMoved: false,
      swipeOffsets: {}, // Зберігає offset для кожної картки
    }
  },
  computed: {
    filteredEvents() {
      if (!this.events) return { current: [], future: [], past: [] };

      if (!this.searchQuery.trim()) {
        return this.events;
      }

      const query = this.searchQuery.toLowerCase().trim();

      return {
        current: this.filterEventsByQuery(this.events.current || [], query),
        future: this.filterEventsByQuery(this.events.future || [], query),
        past: this.filterEventsByQuery(this.events.past || [], query),
      };
    },

    noEvents() {
      return (!this.filteredEvents.current || this.filteredEvents.current.length === 0)
        && (!this.filteredEvents.future || this.filteredEvents.future.length === 0)
        && (!this.filteredEvents.past || this.filteredEvents.past.length === 0);
    }
  },
  mounted() {
    this.fetchEventListForUser();
    // Закривати свайп при кліку поза карткою
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    filterEventsByQuery(events, query) {
      return events.filter(event =>
        event.name.toLowerCase().includes(query)
      );
    },

    goToEvent(eventId) {
      accessAPI.authorizeUserByEvent(eventId)
        .then(async (res) => {
          const token = res.data.data.token;
          const attendeeId = res.data.data.attendee_id;

          if (token) {
            await storage.setItem('token', token);
            await storage.setItem('attendee_id', attendeeId);

            await setHeaderAuth(token);

            await this.$store.dispatch("attendeeStore/fetchAttendee", {
              id: attendeeId
            });

            this.$store.commit('agendaStore/updateHasFetchedData', false);

            this.$router.push({
              name: 'Full Schedule',
              params: {
                attendeeId: attendeeId
              }
            });
          }
        })
        .catch(() => {})
        .finally(() => {});
    },

    fetchEventListForUser() {
      accessAPI.fetchEventListForUser()
        .then((res) => {
          this.events = res.data.data;
        })
        .catch(() => {})
        .finally(() => {});
    },

    openRemoveModal(event) {
      this.currentEvent = event;
      this.showConfirmRemoveModal = true;
      // Закрити свайп
      this.resetSwipe();
    },

    handleDeleteClick(event) {
      this.openRemoveModal(event);
    },

    closeRemoveModal() {
      this.showConfirmRemoveModal = false;
      this.currentEvent = null;
    },

    removeEvent() {
      if (!this.currentEvent) return;

      this.showDeleteIndicator = true;

      accessAPI.hideEventForUser(this.currentEvent.id)
        .then(() => {
          if (this.events?.past) {
            this.events.past = this.events.past.filter(
              event => event.id !== this.currentEvent.id
            );
          }
        })
        .catch(() => {})
        .finally(() => {
          this.showDeleteIndicator = false;
          this.showConfirmRemoveModal = false;
        });
    },

    // Обробка кліку на картку (для Current і Future events)
    handleCardClick(eventId) {
      this.goToEvent(eventId);
    },

    // Обробка кліку на past event
    handlePastEventClick(event) {
      // Запобігаємо переходу якщо був свайп
      if (this.hasMoved) {
        return;
      }

      // На десктопі дозволяємо клік по картці
      if (window.innerWidth > 800) {
        this.goToEvent(event.id);
        return;
      }

      // На мобільних:
      // Якщо картка зсунута (відкрита кнопка delete), закриваємо її
      if (this.swipeOffsets[event.id] && this.swipeOffsets[event.id] !== 0) {
        this.resetSwipe();
        return;
      }

      // Інакше переходимо до івенту
      this.goToEvent(event.id);
    },

    // Swipe методи:
    onTouchStart(event, eventData) {
      if (window.innerWidth > 800) return; // Тільки для мобільних

      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = false;
      this.hasMoved = false;
      this.swipedEventId = eventData.id;
    },

    onTouchMove(event) {
      if (window.innerWidth > 800) return;

      const touchCurrentX = event.touches[0].clientX;
      const touchCurrentY = event.touches[0].clientY;

      const diffX = this.touchStartX - touchCurrentX;
      const diffY = Math.abs(this.touchStartY - touchCurrentY);

      // Визначаємо свайп тільки якщо горизонтальний рух більший за вертикальний
      // та більший за поріг у 10px
      if (Math.abs(diffX) > 10 && Math.abs(diffX) > diffY) {
        this.isSwiping = true;
        this.hasMoved = true;
        // Запобігаємо скролу тільки під час свайпу
        event.preventDefault();
      }

      if (!this.isSwiping) return;

      this.touchCurrentX = touchCurrentX;

      // Свайп тільки вліво і максимум 100px
      if (diffX > 0 && diffX <= 100) {
        this.$set(this.swipeOffsets, this.swipedEventId, -diffX);
      }
    },

    onTouchEnd() {
      if (window.innerWidth > 800) return;

      if (!this.isSwiping) {
        // Якщо не було свайпу, скидаємо hasMoved після невеликої затримки
        // щоб клік встиг спрацювати
        setTimeout(() => {
          this.hasMoved = false;
        }, 50);
        return;
      }

      const diff = this.touchStartX - this.touchCurrentX;

      // Якщо свайп більше 50px - показуємо кнопку, інакше повертаємо назад
      if (diff > 50) {
        this.$set(this.swipeOffsets, this.swipedEventId, -80);
      } else {
        this.$set(this.swipeOffsets, this.swipedEventId, 0);
      }

      // Затримка для завершення анімації та запобігання випадковому кліку
      setTimeout(() => {
        this.isSwiping = false;
        this.hasMoved = false;
      }, 300);
    },

    getCardStyle(eventId) {
      const offset = this.swipeOffsets[eventId] || 0;
      return {
        transform: `translateX(${offset}px)`,
        transition: this.isSwiping ? 'none' : 'transform 0.3s ease'
      };
    },

    resetSwipe() {
      this.swipeOffsets = {};
      this.swipedEventId = null;
      this.isSwiping = false;
      this.hasMoved = false;
    },

    handleOutsideClick(event) {
      if (window.innerWidth > 800) return;

      // Якщо клік не на картці з відкритим свайпом
      if (!event.target.closest('.event-card-wrapper')) {
        this.resetSwipe();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.access {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: var(--attendee-gap-md);

  &__button {
    text-align: right;
    margin-top: var(--attendee-gap-lg);
  }

  &__logo {
    display: block;
    text-align: center;
    margin: var(--attendee-gap-lg) auto;
  }

  &__card {
    width: 100%;
    max-width: 800px;
    padding: var(--attendee-gap-lg);
    border: 1px solid var(--attendee-border-color);
    border-radius: var(--attendee-card-border-radius);
    background-color: var(--attendee-card-color);
  }

  &__title {
    font-size: 22px;
    color: var(--attendee-text-primary-color);
    margin-bottom: var(--attendee-gap-md);
    font-weight: 600;
  }
}

.events-section {
  margin-bottom: var(--attendee-gap-lg);

  &__title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: var(--attendee-gap-md);
    color: var(--attendee-text-primary-color);
    padding-left: 4px;
  }
}

.no-events {
  text-align: center;
  padding: var(--attendee-gap-xl, 2rem);
  color: var(--attendee-text-secondary-color, #666);
}

.event-card-wrapper {
  position: relative;
  margin-bottom: var(--attendee-gap-xs);
  overflow: hidden;
  border-radius: var(--attendee-card-border-radius);
  background-color: #dc3545;
}

.event-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--attendee-gap-sm);
  margin-bottom: var(--attendee-gap-xs);
  background-color: rgba(27, 217, 210, 0.01);
  border-radius: var(--attendee-card-border-radius);
  border: 1px solid var(--attendee-text-secondary-color);
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  min-height: 110px;

  &:active {
    transform: scale(0.98);
  }

  &--past {
    position: relative;
    z-index: 2;
    margin-bottom: 0;
    background-color: #fff;

    &:active {
      transform: none; // Вимикаємо scale для past events щоб не конфліктувало зі свайпом
    }
  }

  &__content {
    display: flex;
    align-items: center;
    gap: var(--attendee-gap-md);
    flex: 1;
    min-width: 0; // Для правильного text-overflow
  }

  &__logo {
    flex-shrink: 0;
    width: 90px;
    height: 90px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;

    img {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
      display: block;
    }
  }

  &__info {
    flex: 1;
    min-width: 0; // Для text-overflow
  }

  &__name {
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 4px 0;
    color: var(--attendee-text-primary-color);
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
  }

  &__date {
    margin: 0;
    color: rgb(189, 187, 187);
    font-size: 14px;
  }

  &__arrow {
    flex-shrink: 0;
    color: rgb(189, 187, 187);
    font-size: 14px;
    margin-left: var(--attendee-gap-sm);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--attendee-gap-sm);
  }
}

.delete-action {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.delete-button {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.desktop-delete-btn {
  background: none;
  border: none;
  color: rgb(189, 187, 187);
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #dc3545;
    background-color: rgba(220, 53, 69, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

.search-box {
  margin-bottom: var(--attendee-gap-lg);
}

// Desktop styles
@media (min-width: 801px) {
  .delete-action {
    display: none;
  }

  .event-card-wrapper {
    overflow: visible;
  }
}

// Mobile styles
@media (max-width: 800px) {
  .access {
    padding: var(--attendee-gap-sm);

    &__card {
      padding: var(--attendee-gap-md);
      border: none;
      box-shadow: none;
    }

    &__title {
      font-size: 20px;
    }
  }

  .events-section {
    &__title {
      font-size: 16px;
      margin-bottom: var(--attendee-gap-sm);
    }
  }

  .desktop-delete-btn,
  .desktop-go-btn {
    display: none !important;
  }

  .event-card {
    &__logo {
      width: 75px;
      height: 75px;
    }

    &__name {
      font-size: 16px;
    }

    &__date {
      font-size: 13px;
    }

    &--past {
      touch-action: pan-y; // Дозволяє вертикальний скрол
    }
  }
}
</style>
